import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider, MutationCache, QueryCache, hashKey } from "@tanstack/react-query";
import { App } from "./App";
import { ConfirmProvider } from "./components/ConfirmDialog";
import { ToastHost, notifyError } from "./components/Toast";
import { supabase } from "./hooks/useAuth";
import { ApiError, empresaAtivaId } from "./lib/api";
import { deveDeslogar, deveTentarDeNovo, esperaTentativa } from "./lib/erroSessao";
import { marcarMotivoDaQueda } from "./lib/quedaDeSessao";
import { applySkin } from "./lib/skin";
import { marcarComoVersaoNova } from "./lib/versaoNova";
import { VigiaDeVersao } from "./components/VigiaDeVersao";
// Fontes locais (substitui o Google Fonts CDN; remove SRI Missing do ZAP).
import "@fontsource/montserrat/300.css";
import "@fontsource/montserrat/400.css";
import "@fontsource/montserrat/500.css";
import "@fontsource/montserrat/600.css";
import "@fontsource/montserrat/700.css";
import "@fontsource/montserrat/800.css";
import "./globals.css";

// Interruptor da pele (redesign v2). Padrão = v2 na M5, v1 nas demais empresas
// (a pele sai do slug espelhado em localStorage, porque aqui no boot ainda não
// há empresa carregada). Só troca aparência; não mexe em comportamento.
// Ver src/client/lib/skin.ts.
applySkin();

// 28/09/2026: import() de CLIQUE (Word da proposta, PDF, zip) numa aba de antes do deploy pedia um chunk que
// não existe mais, e o botão mostrava "Failed to fetch dynamically imported module". O Vite avisa ANTES do erro
// chegar ao botão: o erro passa a dizer o que fazer. Não recarrega aqui (apagaria formulário em andamento);
// quem previne é a VigiaDeVersao, na troca de tela. Ver lib/versaoNova.
window.addEventListener("vite:preloadError", (e) => marcarComoVersaoNova((e as Event & { payload?: unknown }).payload));

const queryClient = new QueryClient({
  // Logout automático SÓ em 401: o token realmente não presta → força novo
  // login em vez de deixar o painel em branco sem aviso.
  //
  // 🔴 28/07 00:42 — o dono foi expulso daqui com um token válido até 01:22.
  // O backend validava o token por CHAMADA DE REDE e devolvia 401 pra qualquer
  // soluço; este `onError` obedecia e deslogava. Agora o servidor separa (401 =
  // token ruim × 503 = não deu pra validar) e a decisão de deslogar mora em
  // `deveDeslogar` — uma função pura que um teste consegue sabotar.
  queryCache: new QueryCache({
    onError: (error) => {
      if (error instanceof ApiError && deveDeslogar(error.status)) {
        // 🔴 UMA CHANCE DE RENOVAR ANTES DE EXPULSAR — 24/08.
        //
        // Medido em `app_errors`: 71 quedas em 14 dias, **49 delas por 401**.
        // A causa da maioria é crachá VENCIDO, não crachá falso — o relógio de
        // renovação do supabase-js não toca com a aba em segundo plano ou a
        // máquina dormindo, então a chamada sai com token morto.
        //
        // 🔑 Vencido e falso chegavam aqui pelo MESMO 401, e este bloco tratava
        // os dois como "some daqui". Renovar custa uma ida à rede; deslogar
        // custa o trabalho da pessoa. Na dúvida, tenta.
        //
        // Se a renovação der certo, o React Query refaz a consulta e ninguém
        // percebe. Se falhar, aí sim o crachá não presta e a saída é legítima.
        void (async () => {
          try {
            const { data } = await supabase.auth.refreshSession();
            if (data?.session) {
              queryClient.invalidateQueries();
              return; // renovou: ninguém sai
            }
          } catch {
            /* cai pra saída abaixo */
          }
          // Quem desloga sabe por quê; quem OUVE o SIGNED_OUT (useAuth) não. Sem
          // este bilhete, esta classe de queda — a única que vem da nossa API —
          // chegaria ao registro como "desconhecido", e o instrumento novo não
          // separaria justamente o caso que dá pra consertar do nosso lado.
          marcarMotivoDaQueda(
            `API devolveu ${error.status} e a renovação NÃO resolveu: ${error.message ?? ""}`.slice(0, 300),
          );
          supabase.auth.signOut();
        })();
      }
    },
  }),
  // Rede de segurança global: toda mutation que falhar e NÃO tiver onError
  // próprio mostra um toast de erro (antes falhava em silêncio). Mutations que
  // já tratam o erro inline não são afetadas (checamos mutation.options.onError).
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      if (mutation.options.onError) return; // já tratada localmente
      const msg = error instanceof Error ? error.message : "Erro inesperado. Tente de novo.";
      notifyError(msg);
    },
  }),
  defaultOptions: {
    queries: {
      // 🔒 TODA CHAVE DE CACHE É POR EMPRESA — sem tocar em 721 call sites.
      //
      // O painel tem 721 `queryKey` e só 39 citavam empresa. As outras 682 eram
      // cegas ao tenant: `["socios-select"]` na M5 e no Vitrine é a MESMA gaveta.
      // Foi por uma dessas que o seletor "Pago por (sócio)" mostrou Carlos/Ana do
      // Vitrine numa despesa da M5 (achado do dono, 27/07).
      //
      // Sair editando 682 chaves na mão seria o remédio pior que a doença: churn
      // gigante, conflito com toda sessão aberta, e a 683ª nasceria errada amanhã.
      // O `queryKeyHashFn` é o ponto ÚNICO onde a chave vira identidade de cache —
      // carimbar a empresa aqui escopa o app inteiro, inclusive o código futuro.
      //
      // `hashKey` é o hash padrão do próprio React Query (stringify estável, com
      // chaves de objeto ordenadas): preservamos a semântica e só prefixamos. Os
      // filtros de `invalidateQueries` continuam funcionando — eles casam pelo
      // ARRAY da chave, não pelo hash.
      queryKeyHashFn: (key) => `${empresaAtivaId() ?? "sem-empresa"}|${hashKey(key)}`,
      // I7 (auditoria 09/06): staleTime aumentado de 15s para 60s.
      // Dashboard tem 12 queries — com 15s cada navegação refazia tudo.
      // 60s: dados operacionais ainda são "frescos o suficiente" pra telas
      // de listagem; queries analíticas pesadas (dashboard) sobrepõem com 5 min.
      staleTime: 60_000,
      // Quando volta pra aba/janela, refetch — pra refletir mudanças feitas
      // em outras abas ou após operações em outras telas.
      refetchOnWindowFocus: true,
      // INSISTIR SÓ NO QUE PASSA SOZINHO (28/07). O padrão do React Query é
      // repetir 3× QUALQUER erro — inclusive 401/403/404, que não melhoram
      // tentando (a pessoa só espera mais pra ver o mesmo erro). E o que
      // realmente passa sozinho — 5xx e rede — merece a insistência: é
      // exatamente o soluço que expulsou o dono do painel.
      retry: (falhas, error) =>
        falhas < 2 && deveTentarDeNovo(error instanceof ApiError ? error.status : 0),
      retryDelay: (tentativa) => esperaTentativa(tentativa),
    },
  },
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <BrowserRouter>
      <VigiaDeVersao />
      <QueryClientProvider client={queryClient}>
        <ConfirmProvider>
          <App />
          <ToastHost />
        </ConfirmProvider>
      </QueryClientProvider>
    </BrowserRouter>
  </React.StrictMode>
);
